'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlDescription } from '../shared/html-description';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { FiArrowRight } from 'react-icons/fi';

export type BlogCardProps = {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: Blog;
  firstBlog?: boolean;
  headingLevel?: 2 | 3;
};

export function BlogCard({ blog, className, firstBlog = false, headingLevel = 3, ...props }: BlogCardProps) {
  const locale = useLocaleStore((state) => state.currentLocale);
  const Heading = headingLevel === 2 ? 'h2' : 'h3';

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4 sm:gap-6 lg:gap-8',
        firstBlog
          ? 'col-span-1 sm:col-span-2 md:mb-2 md:flex-row md:items-center md:justify-between lg:mb-4'
          : 'col-span-1 items-start justify-start',
      )}
    >
      <Link
        prefetch={false}
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
        className={cn(
          'relative h-[205px] w-full max-w-[913px]',
          firstBlog
            ? 'sm:h-[300px] lg:h-[420px] xl:h-[520px]'
            : 'md:h-[240px] lg:h-[300px] xl:h-[440px]',
        )}
      >
        <span className="absolute left-4 top-4 z-10 flex items-center justify-center gap-2 bg-white px-4 py-2 text-xs font-medium text-foreground">
          <FaRegCalendarAlt size={16} />
          {new Date(blog.created_at).toLocaleDateString(locale)}
        </span>
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          sizes="(min-width: 768px) 50vw, 100vw"
          className="absolute inset-0 z-0 h-full w-full"
        />
      </Link>
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <Heading
          data-ea-visual-tag="h4"
          className={cn(
            'font-heading uppercase',
            'w-full text-lg font-medium text-foreground sm:text-xl lg:text-2xl xl:text-3xl',
            'line-clamp-2 h-[54px] sm:h-[58px] lg:h-[64px] xl:h-[74px]',
          )}
        >
          {blog.name}
        </Heading>
        <HtmlDescription
          color="foreground"
          content={blog.content}
          className="ea-blog-card__description line-clamp-3 h-[60px] w-full text-left sm:h-[72px] xl:h-[84px]"
        />
        <div className="flex w-full items-center justify-end">
          <Button asChild className="w-full sm:w-fit">
            <Link prefetch={false} href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
              aria-label={`Prečítať článok: ${blog.name}`}>
              Prečítať článok <FiArrowRight size={20} />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
